import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FiX, FiPlus } from 'react-icons/fi';
import { getAllOrganizations } from 'api/users';
import { getProfileSolutions } from 'api/solutions';

interface ProjectMapping {
  id: string;
  projectName: string;
  role: string;
  organization: string;
}

interface Organization {
  id: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
}

interface MapNewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMapProjects?: (projects: ProjectMapping[]) => void;
}

const MapNewProjectModal: React.FC<MapNewProjectModalProps> = ({
  isOpen,
  onClose,
  onMapProjects,
}) => {
  const [projectMappings, setProjectMappings] = useState<ProjectMapping[]>([
    { id: '1', projectName: '', role: '', organization: '' }
  ]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  const roles = [
    { value: 'owner', label: 'Owner' },
    { value: 'admin', label: 'Admin' },
    { value: 'developer', label: 'Developer' },
    { value: 'designer', label: 'Designer' },
    { value: 'viewer', label: 'Viewer' }
  ];

  // Fetch organizations when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen]);

  const fetchOrganizations = async () => {
    setIsLoading(true);
    try {
      const response = await getAllOrganizations();
      if (response.status === 200 && response.data.list) {
        setOrganizations(response.data.list);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch projects when organization changes
  const fetchProjectsForOrganization = async (organizationId: string) => {
    if (!organizationId) {
      setAvailableProjects([]);
      return;
    }

    setIsLoadingProjects(true);
    try {
      const response = await getProfileSolutions(organizationId);
      if (response.status === 200 && response.data.list) {
        const projects = response.data.list.map((project: any) => ({
          id: project.id,
          name: project.name
        }));
        setAvailableProjects(projects);
      } else {
        setAvailableProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setAvailableProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const addNewRow = () => {
    const newId = (projectMappings.length + 1).toString();
    setProjectMappings([...projectMappings, { id: newId, projectName: '', role: '', organization: '' }]);
  };

  const removeRow = (id: string) => {
    if (projectMappings.length > 1) {
      setProjectMappings(projectMappings.filter(project => project.id !== id));
    }
  };

  const updateProjectMapping = (id: string, field: keyof ProjectMapping, value: string) => {
    setProjectMappings(projectMappings.map(project => {
      if (project.id === id) {
        const updatedProject = { ...project, [field]: value };
        
        // If organization changed, reset project and fetch new projects
        if (field === 'organization') {
          updatedProject.projectName = '';
          fetchProjectsForOrganization(value);
        }
        
        // If project changed, reset role
        if (field === 'projectName') {
          updatedProject.role = '';
        }
        
        return updatedProject;
      }
      return project;
    }));
  };

  const handleMapProjects = () => {
    const validMappings = projectMappings.filter(project => project.projectName && project.role && project.organization);
    if (validMappings.length > 0) {
      if (onMapProjects) {
        onMapProjects(validMappings);
      }
      console.log('Mapping projects:', validMappings);
      onClose();
      // Reset form
      setProjectMappings([{ id: '1', projectName: '', role: '', organization: '' }]);
      setAvailableProjects([]);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset forms
    setProjectMappings([{ id: '1', projectName: '', role: '', organization: '' }]);
    setAvailableProjects([]);
  };

  if (!isOpen) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-md h-[80vh]">
          <div className="bg-[#1A1A1A] rounded-lg border border-white/[0.08] shadow-lg flex flex-col h-full">
            {/* Header - Fixed height */}
            <div className="flex items-center justify-between p-6 border-b border-white/[0.08] flex-shrink-0">
              <h2 className="text-lg font-semibold text-white">
                Map Existing Projects
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Content - Scrollable with fixed height */}
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              <div className="space-y-4">
                {projectMappings.map((project, index) => (
                  <div key={project.id} className="space-y-3 p-3 border border-white/[0.08] rounded-md">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Project #{index + 1}</span>
                      {projectMappings.length > 1 && (
                        <button
                          onClick={() => removeRow(project.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <FiX size={16} />
                        </button>
                      )}
                    </div>
                    
                    {/* Organization Selection - First */}
                    <div>
                      <select
                        value={project.organization}
                        onChange={(e) => updateProjectMapping(project.id, 'organization', e.target.value)}
                        className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                        disabled={isLoading}
                      >
                        <option value="">Select Organization</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Project Selection - Second */}
                    <div>
                      <select
                        value={project.projectName}
                        onChange={(e) => updateProjectMapping(project.id, 'projectName', e.target.value)}
                        className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                        disabled={!project.organization || isLoadingProjects}
                      >
                        <option value="">
                          {!project.organization 
                            ? 'Select Organization First' 
                            : isLoadingProjects 
                              ? 'Loading Projects...' 
                              : availableProjects.length === 0 
                                ? 'No Projects Available' 
                                : 'Select Project'
                          }
                        </option>
                        {availableProjects.map(projectOption => (
                          <option key={projectOption.id} value={projectOption.id}>
                            {projectOption.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Role Selection - Third */}
                    <div>
                      <select
                        value={project.role}
                        onChange={(e) => updateProjectMapping(project.id, 'role', e.target.value)}
                        className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white text-sm"
                        disabled={!project.projectName}
                      >
                        <option value="">
                          {!project.projectName ? 'Select Project First' : 'Select Role'}
                        </option>
                        {roles.map(role => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}

                {/* Add More Button */}
                <button
                  onClick={addNewRow}
                  className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors text-sm"
                >
                  <div className="w-6 h-6 rounded-full bg-green-400/20 flex items-center justify-center">
                    <FiPlus size={14} className="text-green-400" />
                  </div>
                  Add More
                </button>
              </div>
            </div>

            {/* Footer - Fixed position with buttons */}
            <div className="flex justify-end p-6 border-t border-white/[0.08] flex-shrink-0">
              <button
                onClick={handleMapProjects}
                disabled={isLoading || isLoadingProjects}
                className={`px-6 py-2 rounded-md transition-colors font-medium ${
                  isLoading || isLoadingProjects
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-transparent border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-white'
                }`}
              >
                {isLoading || isLoadingProjects ? 'Loading...' : 'MAP PROJECTS'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default MapNewProjectModal;